import { z } from "zod";

/**
 * The lobby: what the room looks at while people join — the join code, the QR code,
 * and the deck's own words of welcome. Set per deck in the editor and frozen with it.
 *
 * `layout` decides what leads: both code and QR side by side, a QR code big enough to
 * scan from the back of a hall, or a code big enough to read from it.
 */
export const LOBBY_LAYOUTS = ["split", "qr", "code"] as const;
export type LobbyLayout = (typeof LOBBY_LAYOUTS)[number];

export const lobbySchema = z
  .object({
    layout: z.enum(LOBBY_LAYOUTS),
    /** Empty: the deck's title. */
    title: z.string().trim().max(120),
    message: z.string().trim().max(200),
  })
  .strict();

export type Lobby = z.infer<typeof lobbySchema>;

export const DEFAULT_LOBBY: Lobby = { layout: "split", title: "", message: "" };

/** Whatever a deck or snapshot holds, as a usable lobby — field by field, like readTheme. */
export function readLobby(value: unknown): Lobby {
  const v = (value ?? {}) as Record<string, unknown>;
  const pick = <K extends keyof Lobby>(key: K): Lobby[K] => {
    const parsed = lobbySchema.shape[key].safeParse(v[key]);
    return parsed.success ? (parsed.data as Lobby[K]) : DEFAULT_LOBBY[key];
  };
  return { layout: pick("layout"), title: pick("title"), message: pick("message") };
}
